/*
 * <copyright company="Argenbright Innovations Lab">
 *        copyright (c) Argenbright Innovations Lab, an Argenbright Holdings Company.  All rights reserved.
 * </copyright>
 */
/* eslint-disable sonarjs/cognitive-complexity */
import React, { isValidElement, useCallback, useEffect, useMemo, useState } from 'react';
import { Box, IconButton, Select, SelectChangeEvent, Stack, Typography } from '@mui/material';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import { MRT_ColumnDef } from 'material-react-table';
import { useTranslation } from 'react-i18next';
import { isEmpty } from 'lodash';
import { utc } from 'moment';

import { getStoredCustomerDetails, NUMBER_OF_ITEMS_PER_PAGE } from '../../../../Shared/Utilities/utils';
import { StyleConstants } from '../../../../Shared/Constants/Style.Constants';
import {
  CurrencySymbol,
  DATA_STATUS,
  FilterItem,
  NumberOfRowsOptions,
  SortOrderType,
  states,
  Paths,
  DEFAULT_PAGE_NUMBER,
  ITEMS_PER_PAGE,
  DateFormat,
} from '../../../../Shared/Constants/App';

import { PendingCustomerApprovalOneTimeJobRow, SortingItem, LocationState } from './PendingCustomerApprovalJobs.types';
import { TaskData } from '../../../../Shared/Models/WorkTask.model';
import { IJobRow, IJobs } from '../../../Jobs/Models/JobsView.Model';

import JobCreateApiService from '../../../Jobs/Services/JobCreateService';
import PageTitle from '../../../../Shared/Components/Common/PageTitle/PageTitle';
import DataGrid from '../../../../Shared/Components/Common/DataGrid/DataGrid';
import { IJobOverview } from '../../../Jobs/Components/JobsListView/JobsListView';
import { IBidList } from '../../../Jobs/Components/JobDetails/OneTimeJobDetails';
import { IProfileCardProps } from '../ViewFacilityDetails/ViewFacilityDetails';
import { Labels } from '../../../../Shared/Components/Common/GraphContainer/GraphContainer';
import { Icons } from '../../../../Shared/Constants/Icons';
import theme from '../../../../Shared/Themes/theme';
import {
  ActionsColumn,
  JobLink,
  Linking,
  Menus,
  MenuWrapper,
} from '../../../../Shared/Components/Common/DataGrid/DataGrid.styles';
import { CustomerContainer } from '../../../../Shared/Components/Layout/styles';
import { Options } from '../../../../Shared/Components/Common/Dropdown/Dropdown.styles';
import { StyledPagination } from '../../../../Shared/Components/Common/CommonStyle/Pagination.Style';
import { DropDown, SelectInput } from '../../../../Shared/Components/Common/GraphContainer/GraphContainer.styles';

const PendingCustomerApprovalPage = (): JSX.Element => {
  const { t } = useTranslation(['oneTimeJob', 'dashboard', 'tableHeader', 'altText', 'vendor', 'homePage']);
  const { customerId = '', userId } = getStoredCustomerDetails() || {};
  const [pendingCustomerApprovalJobs, setPendingCustomerApprovalJobs] = useState<
    PendingCustomerApprovalOneTimeJobRow[]
  >([]);
  const [sortBy, setSortBy] = useState('');
  const [columnFilters, setColumnFilters] = useState<FilterItem[]>([]);
  const [clickedRow, setClickedRow] = useState<number | null>(null);
  const [rowInfo, setRowInfo] = useState<PendingCustomerApprovalOneTimeJobRow>(
    {} as PendingCustomerApprovalOneTimeJobRow
  );
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [sorting, setSorting] = useState<SortingItem[]>([]);
  const [newSortOrder, setNewSortOrder] = useState('');
  const [newSort, setNewSort] = useState<string>();
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const open = Boolean(anchorEl);
  const [currentPage, setCurrentPage] = useState(DEFAULT_PAGE_NUMBER);
  const [itemsPerPage, setItemsPerPage] = useState(ITEMS_PER_PAGE);
  const [totalCount, setTotalCount] = useState(0);
  const [isValueOpen, setValueOpen] = useState(false);

  const handleClick = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const myCustomSortingFn = () => {
    setSortBy(SortOrderType.DESC);
  };

  const sortingJobs = (sortingObj: SortingItem[]) => {
    setSorting(sortingObj);

    if (sortingObj?.length) {
      setNewSortOrder(sortingObj?.[0]?.id);
      setNewSort(sortingObj?.[0]?.desc ? 'desc' : 'asc');
    }
  };

  const fetchPendingCustomerApprovalJobs = useCallback(
    async (
      currentPage?: number,
      limit?: number,
      sortBy?: string,
      sortOrder?: string,
      columnFilters: FilterItem[] = []
    ) => {
      try {
        if (customerId && userId) {
          setIsLoading(true);
          const jobsDataResponse = await JobCreateApiService.getPendingCustomerApprovalOneTimeJobs(userId, customerId, {
            pageIndex: currentPage ?? DEFAULT_PAGE_NUMBER,
            limit,
            sortBy,
            sortOrder: sortOrder ?? '',
            columnFilters,
          });

          if (!isEmpty(jobsDataResponse?.data)) {
            setPendingCustomerApprovalJobs(jobsDataResponse?.data?.data);
            setTotalCount(jobsDataResponse?.data?.metadata?.totalCount);
          }
        }
      } catch (error) {
        setIsLoading(false);
      } finally {
        setIsLoading(false);
      }
    },
    [customerId, userId]
  );

  const renderJobLinkCell = (
    renderedCellValue: string | React.ReactNode | null | undefined,
    row: any
  ): JSX.Element | '-' => {
    const { jobId } = row?.original as unknown as IJobRow;

    const route = Paths.NavigateToBid;
    const state = {
      facilityItem: row?.original,
      facility: {
        ...(row?.original as unknown as PendingCustomerApprovalOneTimeJobRow)?.facility,
        address: (row?.original as unknown as PendingCustomerApprovalOneTimeJobRow)?.address,
        facilityType: (row?.original as unknown as PendingCustomerApprovalOneTimeJobRow)?.facilityType,
      },
      job: row?.original,
    };

    if (typeof renderedCellValue === 'string') {
      return (
        <JobLink to={`${route}?id=${jobId}`} state={state}>
          {renderedCellValue}
        </JobLink>
      );
    }

    return '-';
  };

  const renderCell = (renderedCellValue: React.ReactNode): string | number => {
    if (typeof renderedCellValue === 'string' || typeof renderedCellValue === 'number') {
      return renderedCellValue ? renderedCellValue : '-';
    }
    if (renderedCellValue && Array.isArray(renderedCellValue)) {
      const firstItem = renderedCellValue[0] as { finalQuote?: number };
      return firstItem?.finalQuote || firstItem?.finalQuote === 0
        ? `${CurrencySymbol.Dollar}${Number(firstItem?.finalQuote).toFixed(2)}`
        : '-';
    }
    return '-';
  };

  const formatDateCell = (cellValue: string | Date): string => {
    if (cellValue) {
      return utc(cellValue).format(DateFormat.StandardDate);
    }
    return '-';
  };

  const renderStatusCell = (renderedCellValue: string | React.ReactNode | undefined): JSX.Element | string => {
    if (typeof renderedCellValue === 'string') {
      return <span>{DATA_STATUS[renderedCellValue as keyof typeof DATA_STATUS]}</span>;
    }

    if (isValidElement(renderedCellValue)) {
      return renderedCellValue;
    }

    return '-';
  };

  const renderActionsCell = (row: any): JSX.Element => {
    const originalRow = row?.original as unknown as PendingCustomerApprovalOneTimeJobRow;

    const locationState: LocationState = {
      facilityItem: { ...originalRow, estimationType: 'estimateWithinPeazy', executionType: 'self' },
      jobStatus: originalRow?.status,
      facility: {
        ...originalRow?.facility,
        address: originalRow?.address,
        facilityType: originalRow?.facilityType,
      },
      job: { ...originalRow, customerId },
    };

    return (
      <ActionsColumn>
        <IconButton
          onClick={(e) => {
            handleClick(e);
            setClickedRow(row?.index);
            setRowInfo(originalRow);
          }}
          key={row?.index}
          disabled={false}
          sx={{
            backgroundColor: clickedRow === row?.index && open ? theme.palette.primary.main : 'unset',
            '&:hover': {
              background: theme.palette.primary.main,
            },
            opacity: 1,
          }}
          size="large"
          aria-haspopup="true"
          {...(open && { 'aria-expanded': 'true', 'aria-controls': 'account-menu' })}>
          <MoreVertIcon sx={{ color: theme.palette.common.black }} />
        </IconButton>
        {clickedRow === row?.index && (
          <MenuWrapper anchorEl={anchorEl} id="account-menu" open={open} onClose={handleClose} onClick={handleClose}>
            <Linking
              to={{ pathname: `${Paths.OneTimeJobDetails}`, search: `?id=${rowInfo?.jobId}` }}
              state={locationState}
              onClick={() => localStorage.setItem('locationState', JSON.stringify(locationState))}>
              <Menus>
                <img src={Icons.EyeIcon} alt={t('altTexts:viewDetails')} />
                {t('dashboard:viewDetails')}
              </Menus>
            </Linking>
            <Linking
              to={Paths.BidApprovalEmail}
              state={{
                isFromEmail: false,
                bidId: originalRow?.bidId,
                jobId: originalRow?.jobId,
                jobName: originalRow?.jobName,
              }}>
              <Menus>
                <img src={Icons.ConditionCheckCorrectIcon} alt={t('dashboard:approveButtonText')} />
                {t('dashboard:approveButtonText')}
              </Menus>
            </Linking>
            <Linking
              to={Paths.BidApprovalEmail}
              state={{
                isFromEmail: false,
                bidId: originalRow?.bidId,
                jobId: originalRow?.jobId,
                jobName: originalRow?.jobName,
              }}>
              <Menus>
                <img src={Icons.RequestBid} alt={t('dashboard:requestRevision')} />
                {t('dashboard:requestRevision')}
              </Menus>
            </Linking>
          </MenuWrapper>
        )}
      </ActionsColumn>
    );
  };

  const handleItemsPerPageChange = (event: SelectChangeEvent<number>) => {
    const newItemsPerPage = Number(event?.target?.value);
    setItemsPerPage(newItemsPerPage);
    setCurrentPage(DEFAULT_PAGE_NUMBER);
    fetchPendingCustomerApprovalJobs(currentPage, newItemsPerPage, newSortOrder, newSort);
  };

  const renderFinalQuoteCell = (renderedCellValue: React.ReactNode) => {
    if (typeof renderedCellValue === 'string' || typeof renderedCellValue === 'number') {
      return parseFloat(String(renderedCellValue)).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
    }
    return '-';
  };

  const oneTimeJobColumns = useMemo<MRT_ColumnDef<IJobOverview | TaskData | IJobs | IBidList | IProfileCardProps>[]>(
    () => [
      {
        accessorKey: 'jobDescription',
        header: t('tableHeader:systemJobName'),
        enableSorting: true,
        Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
      },
      {
        accessorKey: 'jobName',
        header: t('tableHeader:otjJobDescription'),
        enableSorting: true,
        state: { sortBy, columnFilters },
        enableColumnFilter: true,
        muiEditTextFieldProps: {
          required: true,
          type: 'number',
          variant: 'outlined',
        },
        size: 210,
        Cell: ({ renderedCellValue, row }) => (
          <div style={{ color: theme.palette.primary.dark }}>{renderJobLinkCell(renderedCellValue, row)}</div>
        ),
        enableColumnActions: false,
        enableHiding: false,
      },
      {
        accessorKey: 'facilityName',
        header: t('tableHeader:facility'),
        enableSorting: true,
        enableColumnFilter: true,
        size: 234,
        Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
        enableColumnActions: false,
        sortingFn: 'alphanumericCaseSensitive',
      },
      {
        accessorKey: 'city',
        header: t('tableHeader:city'),
        enableSorting: false,
        enableColumnFilter: true,
        size: 180,
        Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
        enableColumnActions: false,
        sortingFn: 'alphanumericCaseSensitive',
        enableHiding: false,
      },
      {
        accessorKey: 'state',
        header: t('tableHeader:state'),
        enableSorting: false,
        enableColumnFilter: true,
        size: 180,
        Cell: ({ renderedCellValue }) => <>{states[renderedCellValue as keyof typeof states] || renderedCellValue}</>,
        enableColumnActions: false,
        sortingFn: 'alphanumericCaseSensitive',
        enableHiding: false,
      },
      {
        accessorKey: 'totalCost',
        header: t('tableHeader:finalQuote'),
        enableSorting: false,
        enableColumnFilter: true,
        size: 180,
        Cell: ({ renderedCellValue }) => renderFinalQuoteCell(renderedCellValue),
        enableColumnActions: false,
        sortingFn: 'alphanumericCaseSensitive',
        enableHiding: false,
      },
      {
        accessorKey: 'jobStartDate',
        header: t('calendar:startDate'),
        enableSorting: true,
        enableColumnFilter: false,
        size: 178,
        Cell: ({ cell }) => formatDateCell(cell.getValue<Date | string>()),
        enableColumnActions: false,
        enableHiding: false,
      },
      {
        accessorKey: 'jobEndDate',
        header: t('calendar:endDate'),
        enableSorting: true,
        enableColumnFilter: false,
        size: 178,
        Cell: ({ cell }) => formatDateCell(cell.getValue<Date | string>()),
        enableColumnActions: false,
        enableHiding: false,
      },
      {
        accessorKey: 'numberOfServices',
        header: t('tableHeader:noOfServices'),
        enableSorting: false,
        enableColumnFilter: false,
        size: 178,
        Cell: ({ renderedCellValue }) => renderCell(renderedCellValue),
        enableColumnActions: false,
      },
      {
        accessorKey: 'status',
        header: t('tableHeader:status'),
        enableSorting: false,
        enableColumnFilter: false,
        size: 142,
        Cell: ({ renderedCellValue }) => renderStatusCell(renderedCellValue),
      },
      {
        header: t('tableHeader:actions'),
        enableColumnActions: false,
        size: 90,
        Cell: ({ row }) => renderActionsCell(row),
      },
    ],
    [t, theme, handleClose, sortBy, columnFilters, clickedRow, open, anchorEl, rowInfo]
  );

  const filterOnetimePendingCustomerApprovalJobs = (filtersObj: FilterItem[]): void => {
    setColumnFilters(filtersObj);
    if (filtersObj?.length) {
      fetchPendingCustomerApprovalJobs(currentPage, itemsPerPage, newSortOrder, newSort, filtersObj);
    } else {
      fetchPendingCustomerApprovalJobs(currentPage, itemsPerPage, newSortOrder, newSort, []);
    }
  };

  const toggleValue = () => {
    setValueOpen(!isValueOpen);
  };

  const handleChange = (_event: React.ChangeEvent<unknown>, value: number): void => {
    setCurrentPage(value);
  };

  useEffect(() => {
    setCurrentPage(DEFAULT_PAGE_NUMBER);
    fetchPendingCustomerApprovalJobs();
  }, [customerId, userId]);

  return (
    <>
      <PageTitle title={t('homePage:pendingCustomerApprovalJobs')} />
      <CustomerContainer>
        <>
          <DataGrid
            columns={oneTimeJobColumns}
            data={pendingCustomerApprovalJobs as unknown as TaskData[]}
            enableRowSelect={false}
            enableColumnPinning={false}
            sort={sortingJobs}
            errorMessageTitle={t('noData:noDataYet')}
            errorMessageDescription={t('noData:comeBackLater')}
            sortingFns={{
              myCustomSortingFn: myCustomSortingFn,
            }}
            sortingObj={sorting}
            loader={isLoading}
            filter={filterOnetimePendingCustomerApprovalJobs}
          />
          {!isLoading && (
            <Box sx={{ display: 'flex', justifyContent: StyleConstants.JustifyContentBetween }}>
              {!isLoading && totalCount > ITEMS_PER_PAGE && (
                <Stack spacing={2} justifyContent="center" style={{ marginTop: '20px' }}>
                  <StyledPagination
                    count={Math.ceil(totalCount / itemsPerPage)}
                    page={currentPage}
                    disabled={isLoading}
                    onChange={(event, value) => {
                      handleChange(event, value);
                      if (newSortOrder) {
                        fetchPendingCustomerApprovalJobs(value, itemsPerPage, newSortOrder, newSort);
                      } else {
                        fetchPendingCustomerApprovalJobs(value, itemsPerPage, '', '');
                      }
                    }}
                    color="primary"
                  />
                </Stack>
              )}
              {totalCount > 0 && (
                <Stack spacing={2} justifyContent="center" style={{ marginTop: '20px' }}>
                  <Typography>{`${t('dashboard:totalItems')}: ${totalCount}`}</Typography>
                </Stack>
              )}
              {totalCount > NUMBER_OF_ITEMS_PER_PAGE && (
                <Box sx={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                  <Typography>{t('dashboard:recordsPerPage')}:</Typography>
                  <DropDown>
                    <Box onClick={toggleValue}>
                      <Select
                        value={itemsPerPage}
                        onChange={handleItemsPerPageChange}
                        label={t('altTexts:records')}
                        input={<SelectInput />}
                        IconComponent={() => (
                          <img
                            src={Icons.DropdownIcon}
                            style={{
                              transform: isValueOpen ? Labels.Rotate : 'none',
                              position: 'absolute',
                              right: '0.75rem',
                            }}
                          />
                        )}
                        open={isValueOpen}
                        onClose={toggleValue}
                        onOpen={toggleValue}>
                        {NumberOfRowsOptions.map((dataItem) => (
                          <Options key={dataItem?.id} value={dataItem?.value}>
                            {dataItem?.label}
                          </Options>
                        ))}
                      </Select>
                    </Box>
                  </DropDown>
                </Box>
              )}
            </Box>
          )}
        </>
      </CustomerContainer>
    </>
  );
};

export default PendingCustomerApprovalPage;
